'use client'

import React, { ReactElement, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import FilterControlButtons, {
  FilterControlButtonsProps,
} from '@/components/ui/wizard/filter/FilterControlButtons'
import classNames from 'classnames'
import { ConfigContext } from '@/components/common/ConfigContext'
import { SearchPageContext } from '@/components/pages/search-page/SearchPageContext'
import { useTranslations } from 'next-intl'

interface DropDownProps extends FilterControlButtonsProps {
  children: ReactNode
  rightAligned?: boolean
  id: string
  additionalClassName?: string
}

export default function DropDown(props: DropDownProps): ReactElement {
  const { setSearchOptionsType } = useContext(SearchPageContext)
  const [offset, setOffset] = useState(0)
  const modalRef = useRef<HTMLDivElement>(null)
  const [needsRightAligment, setNeedsRightAligment] = useState(false)
  const [visible, setVisible] = useState(false)
  const timeOutId = useRef<NodeJS.Timeout>()
  const skipHeaderAndFooter = useContext(ConfigContext)!.skipHeaderAndFooter
  const mapLayoutABTestGroupB = true && !skipHeaderAndFooter
  const t = useTranslations('SearchPage')

  useEffect(() => {
    function close(e: MouseEvent): void {
      if (modalRef.current && modalRef.current.contains(e.target as Node)) {
        return
      }

      props.apply()
      setSearchOptionsType(undefined)
    }

    document.addEventListener('click', close)

    return () => document.removeEventListener('click', close)
  }, [props.apply, setSearchOptionsType, modalRef.current])

  useEffect(() => {
    function display(): void {
      setVisible(true)
    }

    function repositionDropdown(): void {
      if (!modalRef.current) {
        return
      }

      let id = props.id
      let rightAligned = props.rightAligned

      if (isPartiallyVisibleInViewport(modalRef.current) || needsRightAligment) {
        setNeedsRightAligment(true)
        id = 'order'
        rightAligned = true
      }

      const button = document.querySelector(`#filter-button-${id}`) as unknown as HTMLButtonElement

      const buttonWidth = button?.clientWidth || 0
      const buttonOffset = button?.offsetLeft || 0
      const offsetWidth = modalRef.current?.offsetWidth || 0

      setOffset(rightAligned ? buttonOffset - offsetWidth + buttonWidth : buttonOffset)
      clearTimeout(timeOutId.current)
      timeOutId.current = setTimeout(display, 10)
    }

    repositionDropdown()

    window.addEventListener('resize', repositionDropdown)

    return () => document.body.removeEventListener('resize', repositionDropdown)
  }, [modalRef.current, needsRightAligment])

  function apply(): void {
    props.apply()
    setSearchOptionsType(undefined)
  }

  function isPartiallyVisibleInViewport(element: HTMLDivElement): boolean {
    const bounds = element.getBoundingClientRect()
    const documentBounds = window.document.documentElement.getBoundingClientRect()
    return bounds.right > documentBounds.right
  }

  return (
    <div
      className={classNames(
        'absolute mt-2 hidden min-h-[180px] min-w-[400px] cursor-default rounded-md bg-white text-left drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]',
        props.additionalClassName,
        { 'top-[56px] z-10': mapLayoutABTestGroupB },
        { 'top-[100%]': !mapLayoutABTestGroupB },
      )}
      style={{
        left: offset,
        display: offset ? 'block' : 'hidden',
        visibility: visible ? 'visible' : 'hidden',
      }}
      ref={modalRef}
      id={props.id}
    >
      <div
        className={classNames('overflow-hidden px-4 pt-4', {
          'max-h-[calc(100vh-200px)]': !mapLayoutABTestGroupB,
          'max-h-[calc(100vh-230px)]': mapLayoutABTestGroupB,
        })}
      >
        {props.children}
      </div>

      <FilterControlButtons reset={props.reset} apply={apply} resetLabel={t('filterOptionReset')} />
    </div>
  )
}
